/**
 * Timesheets + time entries — reads AND writes through the Hono backend
 * (`/api/v1/timesheets/orgs/:orgId/*`, apps/api/src/routes/projects/timesheets.ts).
 * No direct Supabase: single-row timesheet writes are Pattern B on the
 * caller's user client (RLS is the permission layer — a member edits only
 * their own entries, org admins and accountants anyone's), the multi-row entry
 * save is the atomic `save_time_entries` RPC (Pattern B+). Request bodies and
 * coded errors are the shared `timesheetsWrites` schemas.
 *
 * Query keys are a liveness contract (see `useTimesheetIndex.ts`): every
 * writer invalidates the `['timesheets']` prefix the index lives under.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateTimesheetInput as CreateTimesheetBody,
  SaveTimeEntriesInput,
  SaveTimeEntriesResponse,
  TimeEntryCreateInput,
  TimeEntryField,
  TimeEntryUpdateInput,
  TimesheetDeleteResponse,
  TimesheetResponse,
  TimesheetVisibility,
  TimesheetWire,
  UpdateTimesheetInput as UpdateTimesheetBody,
} from '@releve/shared';
import { TIME_ENTRY_FIELDS, TIMESHEET_ERROR_CODES } from '@releve/shared';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/useToast';
import { authedDelete, authedGet, authedPatch, authedPost } from '@/lib/api';
import { TIMESHEET_MUTATION_META, timesheetErrorCodeOf, toastTimesheetError } from '@/lib/timesheetErrors';
import { calculateHours, formatTimeHHmm } from '@/lib/timeUtils';
import i18n from '@/i18n';

export type VisibilityLevel = TimesheetVisibility;

/** One timesheet as the API returns it. */
export type Timesheet = TimesheetWire;

export interface TimeEntry {
  id: string;
  timesheet_id: string;
  organization_id: string;
  project_id: string | null;
  person_id: string | null;
  business_id: string | null;
  task: string | null;
  entry_date: string;
  start_time: string;
  end_time: string | null; // NULL indicates an active (clocked in) entry
  is_estimated: boolean;
  is_billable: boolean;
  note: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Calculated field (not in DB)
  hours?: number;
}

/** A time entry with the display relations the entries read embeds. */
export interface TimeEntryWithDisplay extends TimeEntry {
  project: { id: string; name: string; code: string } | null;
  person: { id: string; first_name: string; last_name: string } | null;
  business: { id: string; name: string; code: string } | null;
}

export type CreateTimesheetInput = CreateTimesheetBody;

/** A PATCH body plus the timesheet it targets. */
export type UpdateTimesheetInput = UpdateTimesheetBody & { id: string };

/** The API base for one org's timesheet routes. */
function timesheetsBase(organizationId: string): string {
  return `/api/v1/timesheets/orgs/${organizationId}`;
}

/**
 * Hook to fetch a single timesheet by ID (`GET …/timesheets/:id`) — the edit
 * dialog and the detail page. Key `['timesheet', id]`, invalidated by
 * `useUpdateTimesheet`.
 */
export function useTimesheet(timesheetId: string | undefined) {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useQuery({
    queryKey: ['timesheet', timesheetId],
    queryFn: async () => {
      if (!timesheetId || !organizationId) return null;
      const result = await authedGet<TimesheetResponse>(
        `${timesheetsBase(organizationId)}/timesheets/${timesheetId}`,
      );
      return result.timesheet;
    },
    enabled: !!timesheetId && !!organizationId,
  });
}

/**
 * The COMPLETE entry list of one timesheet, newest first, with each entry's
 * `hours`. Read through the API (`GET …/timesheets/:id/entries`), which
 * keyset-pages past PostgREST's silent 1,000-row cap — the detail page derives
 * per-month running balances over the whole set, so a truncated list would
 * show wrong totals. `from` (YYYY-MM-DD) limits it to entries on or after that
 * day; the expanded row's summary uses it for its trailing window.
 */
export function fetchTimesheetEntries<T = TimeEntryWithDisplay>(
  organizationId: string,
  timesheetId: string,
  from?: string,
): Promise<T[]> {
  const query = from ? `?from=${encodeURIComponent(from)}` : '';
  return authedGet<{ items: T[] }>(
    `${timesheetsBase(organizationId)}/timesheets/${timesheetId}/entries${query}`,
  ).then((res) => res.items);
}

/**
 * Hook to fetch time entries for a timesheet
 */
export function useTimeEntries(timesheetId: string | undefined) {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useQuery({
    // Key stays ['time_entries', timesheetId]: the entry writers invalidate it.
    queryKey: ['time_entries', timesheetId],
    queryFn: async () => {
      if (!timesheetId || !organizationId) return [];
      const entries = await fetchTimesheetEntries(organizationId, timesheetId);
      return entries.map(entry => ({
        ...entry,
        hours: calculateHours(entry.start_time, entry.end_time),
      }));
    },
    enabled: !!timesheetId && !!organizationId,
  });
}


/** Column defaults for a new timesheet when the caller sends none. */
const DEFAULT_COLUMN_CONFIG: Record<string, boolean> = {
  is_estimated: true,
  is_billable: true,
  project_id: true,
  person_id: true,
  business_id: true,
  entry_date: true,
  start_time: true,
  end_time: true,
  note: true,
};

const DEFAULT_COLUMN_ORDER = [
  'is_estimated', 'is_billable', 'project_id', 'person_id',
  'business_id', 'entry_date', 'start_time', 'end_time', 'hours', 'note',
];

/**
 * Hook to create a new timesheet (`POST …/timesheets`). `created_by` is the
 * auth user — the server sets it and RLS enforces it.
 */
export function useCreateTimesheet() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    meta: TIMESHEET_MUTATION_META,
    mutationFn: async (input: CreateTimesheetInput): Promise<Timesheet> => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');
      const body: CreateTimesheetBody = {
        ...input,
        column_config: input.column_config ?? DEFAULT_COLUMN_CONFIG,
        column_order: input.column_order ?? DEFAULT_COLUMN_ORDER,
        visibility: input.visibility ?? 'members',
      };
      const result = await authedPost<TimesheetResponse>(
        `${timesheetsBase(selectedOrganization.id)}/timesheets`,
        body,
      );
      return result.timesheet;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      toast.success(i18n.t('timesheets:toasts.created'));
    },
    onError: (error) => toastTimesheetError(error, 'timesheets:toasts.createFailed'),
  });
}

/**
 * Hook to update a timesheet (`PATCH …/timesheets/:id`) — present keys are
 * written, absent keys left alone.
 */
export function useUpdateTimesheet() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    meta: TIMESHEET_MUTATION_META,
    mutationFn: async ({ id, ...patch }: UpdateTimesheetInput): Promise<Timesheet> => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');
      const body = Object.fromEntries(
        Object.entries(patch).filter(([, value]) => value !== undefined),
      ) as UpdateTimesheetBody;
      const result = await authedPatch<TimesheetResponse>(
        `${timesheetsBase(selectedOrganization.id)}/timesheets/${id}`,
        body,
      );
      return result.timesheet;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      queryClient.invalidateQueries({ queryKey: ['timesheet', data.id] });
      // The running clock card shows the timesheet's name.
      queryClient.invalidateQueries({ queryKey: ['active-time-entry'] });
      toast.success(i18n.t('timesheets:toasts.updated'));
    },
    onError: (error) => toastTimesheetError(error, 'timesheets:toasts.updateFailed'),
  });
}

/**
 * Hook to delete a timesheet (`DELETE …/timesheets/:id`). Deleting a sheet
 * that holds other people's hours is an org-admin action: anyone else gets the
 * coded 409 `timesheet_has_others_entries`, toasted with its own sentence.
 */
export function useDeleteTimesheet() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    meta: TIMESHEET_MUTATION_META,
    mutationFn: async (timesheetId: string): Promise<void> => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');
      await authedDelete<TimesheetDeleteResponse>(
        `${timesheetsBase(selectedOrganization.id)}/timesheets/${timesheetId}`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      // Its entries went with it (ON DELETE CASCADE) — a running clock included.
      queryClient.invalidateQueries({ queryKey: ['active-time-entry'] });
      queryClient.invalidateQueries({ queryKey: ['todays-entries'] });
      queryClient.invalidateQueries({ queryKey: ['time_entries'] });
      toast.success(i18n.t('timesheets:toasts.deleted'));
    },
    onError: (error) => toastTimesheetError(error, 'timesheets:toasts.deleteFailed'),
  });
}

/** What `save_time_entries` returned. */
export type BatchSaveResult = SaveTimeEntriesResponse;

/** One staged grid change — `RowChange` from the editable table. */
export interface TimeEntryChange {
  type: 'create' | 'update' | 'delete';
  rowId?: string;
  /** The full row (the grid hands over every column, not only the edited ones). */
  data: Partial<TimeEntry>;
  /** The row as loaded — an update sends only the keys that differ from it. */
  originalData?: Partial<TimeEntry>;
}

const UUID_FIELDS = new Set<TimeEntryField>(['project_id', 'person_id', 'business_id']);
const TIME_FIELDS = new Set<TimeEntryField>(['start_time', 'end_time']);
const TEXT_FIELDS = new Set<TimeEntryField>(['task', 'note']);

/**
 * A field's value as it goes on the wire: `''` uuid → null, blank text →
 * null (the server's `blankToNull`, so a cell cleared back to empty is not a
 * change), times as `'HH:mm'`.
 */
function wireValue(key: TimeEntryField, value: unknown): unknown {
  if (UUID_FIELDS.has(key) && value === '') return null;
  if (TEXT_FIELDS.has(key) && typeof value === 'string' && value.trim() === '') return null;
  if (TIME_FIELDS.has(key)) return formatTimeHHmm(value as string | null | undefined);
  return value;
}

/** The writable fields present on `data`, normalised for the wire. */
function pickTimeEntryFields(data: Partial<TimeEntry>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of TIME_ENTRY_FIELDS) {
    if (key in data) result[key] = wireValue(key, data[key]);
  }
  return result;
}

/**
 * The CHANGED writable fields of an update: keys whose normalised value
 * differs from `originalData` (all present keys when there is no original).
 * Sending the whole row would rewrite untouched columns — and an unchanged
 * NULL `end_time` on a running entry would trip the save's "end time
 * required" rule.
 */
function changedTimeEntryFields(
  data: Partial<TimeEntry>,
  originalData: Partial<TimeEntry> | undefined,
): Record<string, unknown> {
  const picked = pickTimeEntryFields(data);
  if (!originalData) return picked;
  const original = pickTimeEntryFields(originalData);
  return Object.fromEntries(
    Object.entries(picked).filter(([key, value]) => (original[key] ?? null) !== (value ?? null)),
  );
}

/** The save body for a set of staged changes (exported for tests). */
export function buildSaveTimeEntriesBody(entries: TimeEntryChange[]): Required<SaveTimeEntriesInput> {
  const creates = entries
    .filter((e) => e.type === 'create')
    .map((e) => pickTimeEntryFields(e.data) as TimeEntryCreateInput);
  const updates = entries
    .filter((e) => e.type === 'update' && e.rowId)
    .map((e) => ({ id: e.rowId!, ...changedTimeEntryFields(e.data, e.originalData) }) as TimeEntryUpdateInput)
    .filter((u) => Object.keys(u).length > 1);
  const deleteIds = entries
    .filter((e) => e.type === 'delete' && e.rowId)
    .map((e) => e.rowId!);
  return { creates, updates, deleteIds };
}

export interface SaveTimeEntriesOptions {
  /**
   * `false` drops the success toast where the change is visible in place —
   * the phone time clock (docs/MOBILE.md §5.4); failures still toast.
   */
  toastOnSuccess?: boolean;
}

/**
 * Hook to save time entries — one atomic batch of creates, updates and
 * deletes (`POST …/timesheets/:id/entries/save`). Updates carry only their
 * changed keys. A row someone else changed or removed meanwhile (or that is
 * not the caller's to edit) fails the whole batch with the coded 409
 * `timesheet_entries_changed` — "reload".
 */
export function useSaveTimeEntries({ toastOnSuccess = true }: SaveTimeEntriesOptions = {}) {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    meta: TIMESHEET_MUTATION_META,
    mutationFn: async ({ timesheetId, entries }: {
      timesheetId: string;
      entries: TimeEntryChange[];
    }): Promise<BatchSaveResult> => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');

      const body = buildSaveTimeEntriesBody(entries);
      if (body.creates.length + body.updates.length + body.deleteIds.length === 0) {
        // Nothing actually changed (an edit typed back to its old value).
        return { created: [], updated: [], deleted_count: 0 };
      }

      return authedPost<SaveTimeEntriesResponse>(
        `${timesheetsBase(selectedOrganization.id)}/timesheets/${timesheetId}/entries/save`,
        body,
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['time_entries', variables.timesheetId] });
      queryClient.invalidateQueries({ queryKey: ['todays-entries'] });
      queryClient.invalidateQueries({ queryKey: ['active-time-entry'] });
      // Saved entries move `hours_this_month` / `hours_last_month` /
      // `last_entry_date`, all of which the timesheet INDEX projects — so the
      // list has to be invalidated under the prefix `useTimesheetIndex` is
      // keyed under (`['timesheets', orgId, 'index', {}]`).
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      queryClient.invalidateQueries({ queryKey: ['timesheet-summary'] });
      if (toastOnSuccess) toast.success(i18n.t('timesheets:toasts.entriesSaved'));
    },
    onError: (error, variables) => {
      toastTimesheetError(error, 'timesheets:toasts.saveFailed');
      // "Some rows changed — reload": refetch the sheet so the reload is real.
      if (timesheetErrorCodeOf(error) === TIMESHEET_ERROR_CODES.entriesChanged) {
        queryClient.invalidateQueries({ queryKey: ['time_entries', variables.timesheetId] });
        queryClient.invalidateQueries({ queryKey: ['timesheet-summary'] });
      }
    },
  });
}
